import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetStudentDetailsFormData,
  useGetAssignment,
  useSaveUserDetails
} from '../../queries';
import CustomForm from '../userDetails/CustomForm';
import { FullPageLoader } from '../shared';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import AccessDenied from '../AccessDenied';
import { useAuth } from '../../hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';

const FormDataDetails = ({ assignmentId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: formData, isLoading: isFormDataLoading } = useGetStudentDetailsFormData(assignmentId);
  const { isError, error } = useGetAssignment(assignmentId);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const { mutate: saveFormData, isLoading: isSavingFormData } = useSaveUserDetails();

  const hasValidFormData = useCallback((data) => {
    return (
      data &&
      (Array.isArray(data)
        ? data.length > 0
        : typeof data === 'object' && Object.keys(data).length > 0)
    );
  }, []);

  useEffect(() => {
    if (isFormDataLoading) return;

    if (hasValidFormData(formData)) {
      setIsFormModalOpen(true);
    }
  }, [formData, isFormDataLoading, hasValidFormData]);

  const handleFormSubmit = (responses, expandedSections) => {
    if (formData) {
      const formDataPayload = {
        _id: formData._id,
        instituteId: formData.instituteId,
        assignmentId: assignmentId,
        responses,
        expandedSections
      };

      saveFormData(formDataPayload, {
        onSuccess: () => {
          setFormSubmitted(true);
          setTimeout(() => {
            setIsFormModalOpen(false);
          }, 0);
        }
      });
    }
  };

  if (isError && error?.response?.status === 403) {
    return (
      <LoggedInUserWrapper>
        <AccessDenied reason={error?.response?.data?.code} />
      </LoggedInUserWrapper>
    );
  }

  if (isFormDataLoading) {
    return <FullPageLoader />;
  }

  if (formSubmitted || !hasValidFormData(formData)) {
    navigate(`/start/${assignmentId}`);
    return null;
  }

  return (
    <LoggedInUserWrapper>
      {hasValidFormData(formData) && (
        <Dialog
          open={isFormModalOpen}
          onOpenChange={() => {
            // Non-dismissible until submitted
          }}
        >
          <DialogContent
            showCloseButton={false}
            className="flex h-[90vh] max-h-[90vh] w-[calc(100%-1.5rem)] max-w-4xl flex-col gap-0 overflow-hidden rounded-2xl border-border/60 p-0 sm:max-w-5xl"
            onEscapeKeyDown={(event) => event.preventDefault()}
            onPointerDownOutside={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
          >
            <DialogHeader className="sr-only">
              <DialogTitle>{formData?.title || 'Candidate Information'}</DialogTitle>
              <DialogDescription>
                Please fill in your details before starting the assignment.
              </DialogDescription>
            </DialogHeader>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <CustomForm
                formData={formData}
                user={user}
                onSubmit={handleFormSubmit}
                isSubmitting={isSavingFormData}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </LoggedInUserWrapper>
  );
};

export default FormDataDetails;
